import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  FormDynamicApiSelectComponent,
  FormDynamicApiTableComponent,
  FormDatetimeComponent,
  FormTextInputComponent,
  FormSelectComponent,
  MultiCheckboxComponent,
  FormTextareaComponent,
  FormLocationComponent,
  FormPhotoUploadComponent,
  FormAvatarUploadComponent,
  FormDateComponent,
  FormSignatureComponent,
  FormRadiobuttonComponent,
  FormNumberInputComponent,
  FormParagraphComponent,
  FormTimestampComponent,
  FormDepartmentSelectorComponent,
  FormPersonnelSelectorComponent,
  FormTagsComponent,
  FormApprovalFlowComponent,
  FormVideoPlayerComponent,
  FormAudioRecorderComponent,
  MeetingSignatureCollectionComponent,
  FillInFieldComponent,
  FormSubformRelationComponent,
} from '../../items/total-components';

import { FormRichTextDisplayComponent } from '../../items/form-rich-text-display.component';

import { FileDisplayComponent, FileDisplayItem } from '../../items/file-display/file-display.component';
import { FileUploadManagerComponent } from '../../items/file-upload-manager/file-upload-manager.component';
import { FileItem } from '../../items/file-upload-manager/models/file-item.interface';
import { RequiredDocumentUploadComponent } from '../../items/required-document-upload/required-document-upload.component';

import { DatabaseService } from '../../../services/database.service';
import { FormConfigService } from '../../../services/form-config.service';
import { AssistantUrlService } from '../../../services/assistant-url.service';
import { ApprovalService } from '../../items/approval/services/approval.service';
import { InitiatorInfo } from '../../items/approval/models/approval-flow.model';
import { AuthService } from '../../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../services/global-environment';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';
import { StageProgressComponent } from '../../stage-progress/stage-progress.component';
import { DepartmentService } from '../../../services/department.service';
import { FormAutoNumberingComponent } from '../../items/form-auto-numbering/form-auto-numbering.component';

// 定義階段配置的介面
export interface FormStage {
  id: string;
  name: string;
  description: string;
  order: number;
  requiredDepartments: string[];
  allowedUsers: string[];
  fields: string[];
  status: 'pending' | 'in_progress' | 'completed' | 'approved';
  completedBy?: string;
  completedAt?: Date;
}

// 定義增強表單值的介面
export interface EnhancedFormValue {
  type: string;
  value: string | object | any[];  // 顯示名稱或完整物件
  id?: string | any[];            // 唯一ID（可選）
}

// 定義欄位配置的介面
export interface FormFieldConfig {
  type: 'datetime' | 'select' | 'radiobutton' | 'textarea' | 'location' | 'photo-upload' | 'date' | 'signature' | 'text-input' | 'number-input' | 'multi-checkbox' | 'paragraph' | 'timestamp' | 'department-selector' | 'personnel-selector' | 'tags' | 'approval-flow' | 'file-display' | 'audio-recorder' | 'video-player' | 'meeting-signature-collection' | 'file-upload-manager' | 'video-player' | 'fill-in' | 'rich-text-display'| 'dynamic-api-select' | 'dynamic-api-table' | 'subform-relation' | 'required-document-upload' | 'avatar-upload' | 'auto-numbering';
  label: string;
  id: string;
  name: string;
  placeholder?: string;
  options?: any[];
  required?: boolean;
  content?: string; // 新增：用於段落元件的內容
  textAlign?: 'left' | 'center' | 'right' | 'justify'; // 新增：用於段落元件和富文本顯示元件的對齊方式
  format?: 'date' | 'time' | 'datetime'; // 新增：用於時間戳記元件的格式
  multiSelect?: boolean; // 新增：用於人員選擇器的多選模式
  approvalFlowId?: string; // 新增：用於簽核流程元件的流程 ID
  files?: FileDisplayItem[]; // 新增：用於 file-display 元件的檔案資料
  // 新增：用於檔案上傳管理元件的配置
  fileUploadConfig?: {
    maxFiles?: number;
    maxFileSize?: number;
    allowedTypes?: string[];
    enableDragDrop?: boolean;
    enablePreview?: boolean;
    allowRename?: boolean;
    showFileSize?: boolean;
    showUploadTime?: boolean;
    uploadButtonText?: string;
    dragDropText?: string;
  };
  // 新增：用於指定證件上傳元件的配置
  requiredDocumentConfig?: {
    documentName?: string;
    documentDescription?: string;
    aiParsing?: boolean;
    expiryDateField?: string;
    acceptedFormats?: string[];
    maxFileSize?: number;
    uploadHint?: string;
    showFileSize?: boolean;
    showUploadTime?: boolean;
    enableDragDrop?: boolean;
    enablePreview?: boolean;
  };
  // 新增：用於影片播放器元件的屬性
  youtubeUrl?: string; // YouTube 影片連結
  videoTitle?: string; // 影片標題
  videoDescription?: string; // 影片描述
  videoWidth?: string; // 影片寬度
  videoHeight?: string; // 影片高度
  videoAutoplay?: boolean; // 自動播放
  videoControls?: boolean; // 顯示控制項
  videoAllowFullscreen?: boolean; // 允許全螢幕
  maxDuration?: number; // 新增：用於錄音元件的最大時長
  minDuration?: number; // 新增：用於錄音元件的最小時長
  audioQuality?: 'high' | 'medium' | 'low'; // 新增：用於錄音元件的音質設定
  audioFormat?: 'mp3' | 'wav'; // 新增：用於錄音元件的音訊格式
  conditionalInputs?: any; // 新增：用於 radiobutton 元件的條件式輸入配置
  questionImageUrl?: string; // 新增：用於 radiobutton 元件的題目圖片 URL
  questionImageBlobName?: string; // 新增：用於 radiobutton 元件的 Azure Blob 名稱
  questionImageAltText?: string; // 新增：用於 radiobutton 元件的圖片替代文字（無障礙）
  // 新增：用於多人簽名元件的屬性
  documentId?: string; // 文件 ID
  title?: string; // 簽名標題
  participants?: any[]; // 參與者清單
  // 新增：用於填空元件的屬性
  template?: string; // 填空模板
  fields?: any[]; // 填空欄位配置
  // 新增：用於富文本顯示元件的屬性
  fontSize?: string; // 字型大小
  color?: string; // 文字顏色
  lineHeight?: string; // 行間距
  maxWidth?: string; // 最大寬度
  backgroundColor?: string; // 背景顏色
  padding?: string; // 內邊距
  borderRadius?: string; // 圓角設定
  // 新增：用於動態API下拉選單元件的屬性
  apiKey?: string; // API呼叫的key值
  maxSelections?: number; // 動態API表格的最大選擇數量
  // 新增：用於子表單關聯元件的屬性
  formId?: string; // 關聯的子表單ID
  // 新增：用於階段化表單的屬性
  stageId?: string; // 歸屬的階段 ID
  // 新增：用於表單建構器的階段配置（作為陣列的擴展屬性）
  formStages?: FormStage[]; // 表單的階段配置
  // 新增：用於標記系統自動生成的元件（如簽核意見、簽名）
  _isSystemGenerated?: boolean;

  // 新增：用於自動編號元件的配置
  autoNumbering?: {
    elements: Array<{
      type: 'dropdown' | 'sequence' | 'year' | 'month' | 'text';
      order: number;
      config: any;
    }>;
    separator?: string;
    preview?: string;
    apiKey?: string;
    sequenceConfig?: {
      digits: number;
      startFrom: number;
      apiKey?: string;
    };
  };

}

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  // 請根據你的專案補上各通用元件的 import
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // 直接匯入表單元件
    FormDynamicApiSelectComponent,
    FormDynamicApiTableComponent,
    FormDatetimeComponent,
    FormTextInputComponent,
    FormSelectComponent,
    MultiCheckboxComponent,
    FormTextareaComponent,
    FormLocationComponent,
    FormPhotoUploadComponent,
    FormAvatarUploadComponent,
    FormDateComponent,
    FormSignatureComponent,
    FormRadiobuttonComponent,
    FormNumberInputComponent,
    FormParagraphComponent,
    FormTimestampComponent,
    FormDepartmentSelectorComponent,
    FormPersonnelSelectorComponent,
    FormTagsComponent,
    FormApprovalFlowComponent,
    FormVideoPlayerComponent,
    FileDisplayComponent,
    FileUploadManagerComponent,
    RequiredDocumentUploadComponent,
    FormAudioRecorderComponent,
    MeetingSignatureCollectionComponent,
    FillInFieldComponent,
    FormRichTextDisplayComponent,
    FormSubformRelationComponent,
    FormAutoNumberingComponent,
    TranslatePipe,
    StageProgressComponent
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() formName: string = '';
  @Input() assistantid: string = '';
  @Input() assistantname: string = '';
  @Input() config: FormFieldConfig[] = [];
  @Input() preview: boolean = false; //Preview模式主要為在編輯的狀態
  @Input() getuseridlist: boolean = false; // 新增：是否需要取得使用者列表
  @Input() editMode: boolean = false; // 新增：是否為編輯模式
  @Input() editFormId: string = ''; // 新增：編輯的表單ID
  @Input() formid: string = ''; // 新增：表單配置的 ID
  @Input() initialValues: any = {}; // 新增：初始值
  @Input() parent_formid: string = ''; // 新增：父表單ID

  //TODO:這四個屬性要合併
  @Input() approvalMode: boolean = false; // 新增：是否為簽核模式，控制隱藏某些UI元素
  @Input() readonly: boolean = false; // 新增：只讀模式
  @Input() isViewMode: boolean = false; // 新增：查看模式
  @Input() viewFormData: any = {}; // 新增：查看模式下要顯示的表單資料

  @Output() Showlist = new EventEmitter<string>();
  @Output() fillform = new EventEmitter<string>();
  @Output() formSaved = new EventEmitter<boolean>();
  @Output() configChanged = new EventEmitter<FormFieldConfig[]>();
  @Output() recordAdded = new EventEmitter<boolean>();
  @Output() formSubmitted = new EventEmitter<any>(); // 新增：表單提交事件
  @Output() formCancelled = new EventEmitter<void>(); // 新增：表單取消事件

  // 從登入資訊取得
  companyid = "";
  Username = "";
  forminfo: any = {};

  formGroup!: FormGroup;
  formfinished = false;

  // 新增標誌，追蹤是否已嘗試提交表單
  submitAttempted = false;

  // 新增變數，用於編輯 config 的 JSON 字串
  configJSON: string = '';

  showJsonEditor: boolean = false;

  // 段落分組相關屬性
  groupedSections: Array<{
    sectionHeader?: FormFieldConfig;
    fields: FormFieldConfig[];
    isExpanded: boolean;
    completedCount: number;
    totalCount: number;
  }> = [];

  sectionStates: { [key: string]: boolean } = {};

  initiatorInfo: InitiatorInfo = {
    userId: '',
    userName: '',
    departmentId: '',
    departmentName: ''
  };

  // 階段化表單相關屬性
  enableStageMode: boolean = false;
  formStages: FormStage[] = [];
  currentStageIndex: number = 0;
  currentStage: FormStage | null = null;

  constructor(
    private fb: FormBuilder,
    private databaseService: DatabaseService,
    private formConfigService: FormConfigService,
    private assistantUrlService: AssistantUrlService,
    private http: HttpClient,
    private approvalService: ApprovalService,
    private authService: AuthService,
    private i18nService: I18nService,
    private departmentService: DepartmentService
  ) {}

  ngOnInit(): void {

    console.log("DynamicFormComponent initialized with formName:", this.formName);
    console.log("formid:", this.formid);


    this.companyid = environment.companyid;
    this.Username = localStorage.getItem('username') || 'Unknown User';
    this.assistantid = this.assistantUrlService.getAssistantIdFromUrl();

    this.forminfo = {
      companyid: this.companyid,
      username: this.Username,
      assistantid: this.assistantid,
      assistantname: this.assistantname,
      componentname: this.formName,
      createAt: {
        type: 'datetime',
        value: Math.floor(Date.now() / 1000)
      }
    };

    if (this.config && this.preview == true) {
      this.initializeForm();
    } else {
      // 透過 formName 與 companyid 取得表單配置
      this.formConfigService.getFormConfig(this.companyid,this.formid,this.formName)
      .subscribe(async config => {
        this.config = config;
        this.loadStageConfiguration();
        await this.initializeForm();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 config 輸入發生變更時，在預覽模式下發送 configChanged 事件
    if (changes['config'] && !changes['config'].firstChange && this.preview) {
      const currentConfig = changes['config'].currentValue;
      if (currentConfig && Array.isArray(currentConfig)) {
        // 延遲發送事件，避免在初始化期間發送
        setTimeout(() => {
          this.configChanged.emit([...currentConfig]);
        }, 0);
      }
    }

    // 如果 config 變更，重新初始化表單
    if (changes['config'] && this.config) {
      this.initializeForm();
    }
  }

  // 檢查配置中簽核流程元件的數量
  private checkApprovalFlowCount(config: FormFieldConfig[]): number {
    return config.filter(field => field.type === 'approval-flow').length;
  }

  // 自動移除多餘的簽核流程元件
  private limitApprovalFlowComponents(config: FormFieldConfig[]): FormFieldConfig[] {
    let approvalFlowFound = false;
    return config.filter(field => {
      if (field.type === 'approval-flow') {
        if (approvalFlowFound) {
          console.warn('動態表單：移除多餘的簽核流程元件:', field);
          return false; // 移除多餘的簽核流程元件
        }
        approvalFlowFound = true;
      }
      return true;
    });
  }

  // 根據 config 建立 Reactive Form 並更新 configJSON
  async initializeForm(): Promise<void> {

    console.log("initializeForm:", this.config);

    // 檢查並限制簽核流程元件數量
    if (this.config && Array.isArray(this.config)) {
      const approvalFlowCount = this.checkApprovalFlowCount(this.config);
      if (approvalFlowCount > 1) {
        console.warn(`動態表單中檢測到 ${approvalFlowCount} 個簽核流程元件，將限制為1個`);
        this.config = this.limitApprovalFlowComponents(this.config);

        // 只在非預覽模式時顯示警告
        if (!this.preview) {
          setTimeout(() => {
            alert(`表單配置中包含 ${approvalFlowCount} 個簽核流程元件，已自動保留第一個並移除其他 ${approvalFlowCount - 1} 個，每個表單最多只能包含一個簽核流程元件。`);
          }, 100);
        }
      }
    }

    const group: { [key: string]: any } = {};
    this.config.forEach(field => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }

      // 為 file-display 元件設定初始值（如果有預設檔案）
      let initialValue: any = '';
      if (field.type === 'file-display' && field.files && field.files.length > 0) {
        // 不論是否為預覽模式，都要載入預設檔案以供顯示
        initialValue = field.files;
        console.log('載入 file-display 預設檔案:', field.name, field.files);
      } else if (this.isViewMode && this.viewFormData && this.viewFormData.hasOwnProperty(field.name)) {
        // 在查看模式下使用 viewFormData 中的資料
        initialValue = this.viewFormData[field.name];
        console.log(`設定查看模式 ${field.name} 的值:`, initialValue);
      } else if (this.initialValues && this.initialValues.hasOwnProperty(field.name)) {
        // 使用傳入的初始值
        initialValue = this.initialValues[field.name];
        console.log(`設定 ${field.name} 的初始值:`, initialValue);
      } else if ((field as any).initialValue !== undefined) {
        // 使用欄位配置中的初始值
        initialValue = (field as any).initialValue;
        console.log(`使用欄位配置的初始值 ${field.name}:`, initialValue);
      }

      // 為 subform-relation 元件設定初始值（使用 formId）
      if (field.type === 'subform-relation' && field.formId) {
        initialValue = field.formId;
        console.log('載入 subform-relation formId:', field.name, field.formId);
      }

      group[field.name] = [initialValue, validators];

      // 在只讀模式下禁用表單控件
      if (this.readonly || this.isViewMode) {
        // 將會在 FormGroup 建立後進行禁用
      }
    });
    this.formGroup = this.fb.group(group);

    // 在只讀模式下禁用所有表單控件
    if (this.readonly || this.isViewMode) {
      this.formGroup.disable();
    }

    // 重置提交嘗試標誌，確保新表單不會立即顯示驗證錯誤
    this.submitAttempted = false;

    // 將 config 轉為 JSON 格式（縮排2個空格），供 textarea 編輯
    this.configJSON = JSON.stringify(this.config, null, 2);

    // 初始化段落分組
    this.initializeSections();

    // 訂閱表單值變化，用於更新完成狀態
    this.formGroup.valueChanges.subscribe(() => {
      this.updateSectionCompletionCounts();
    });

    // 在預覽模式下，監聽檔案上傳變化並同步到配置
    if (this.preview) {
      this.formGroup.valueChanges.subscribe(formValues => {
        this.syncFileDataToConfig(formValues);
      });
    }

    if( this.getuseridlist) {
  // 新增：檢查是否有簽核流程元件並自動解析
    await this.checkAndResolveApprovalFlow();
    }
  }

  // 初始化段落分組
  initializeSections(): void {
    this.groupedSections = [];
    let currentSection: {
      sectionHeader?: FormFieldConfig;
      fields: FormFieldConfig[];
      isExpanded: boolean;
      completedCount: number;
      totalCount: number;
    } = {
      fields: [],
      isExpanded: true,
      completedCount: 0,
      totalCount: 0
    };

    this.config.forEach(field => {
      if (field.type === 'paragraph') {
        // 如果當前段落有欄位，先推入前一個段落
        if (currentSection.fields.length > 0 || currentSection.sectionHeader) {
          currentSection.totalCount = currentSection.fields.length;
          this.groupedSections.push(currentSection);
        }

        // 開始新段落
        currentSection = {
          sectionHeader: field,
          fields: [],
          isExpanded: this.sectionStates[field.id] !== undefined ? this.sectionStates[field.id] : true,
          completedCount: 0,
          totalCount: 0
        };
      } else {
        // 將非段落欄位加入當前段落
        currentSection.fields.push(field);
      }
    });

    // 推入最後一個段落
    if (currentSection.fields.length > 0 || currentSection.sectionHeader) {
      currentSection.totalCount = currentSection.fields.length;
      this.groupedSections.push(currentSection);
    }

    // 初始化完成狀態計算
    this.updateSectionCompletionCounts();
  }

  // 更新段落完成狀態計算
  updateSectionCompletionCounts(): void {
    if (!this.formGroup) return;

    this.groupedSections.forEach(section => {
      let completedCount = 0;
      section.fields.forEach(field => {
        const control = this.formGroup.get(field.name);
        if (control && this.isFieldCompleted(control.value, field)) {
          completedCount++;
        }
      });
      section.completedCount = completedCount;
    });
  }

  // 判斷欄位是否已完成
  isFieldCompleted(value: any, field: FormFieldConfig): boolean {
    if (value === null || value === undefined) return false;

    // 字串類型欄位
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    // 陣列類型欄位（如多選）
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // 其他類型
    return !!value;
  }

  // 處理段落展開/收合切換
  onSectionToggle(event: {sectionId: string, isExpanded: boolean}): void {
    this.sectionStates[event.sectionId] = event.isExpanded;

    // 更新對應段落的展開狀態
    const section = this.groupedSections.find(s => s.sectionHeader?.id === event.sectionId);
    if (section) {
      section.isExpanded = event.isExpanded;
    }
  }

  toggleJsonEditor(): void {
    this.showJsonEditor = !this.showJsonEditor;
  }

  // 解析 configJSON 並重新建立表單
  async reloadForm(): Promise<void> {
    try {
      const newConfig = JSON.parse(this.configJSON);
      this.config = newConfig;
      await this.initializeForm();

      // 發送配置變更事件，通知父元件同步更新
      this.configChanged.emit([...newConfig]);
    } catch (error) {
      console.error('Invalid JSON config:', error);
      alert('Invalid JSON format. Please check your config.');
    }
  }



  onSubmit(): void {
    // 標記為已嘗試提交表單
    this.submitAttempted = true;

    // 檢查表單是否有效
    if (this.formGroup.invalid) {
      // 收集所有無效的必填欄位
      const invalidRequiredFields = this.getInvalidRequiredFields();

      if (invalidRequiredFields.length > 0) {
        const fieldNames = invalidRequiredFields.map(field => field.label).join('、');
        const errorMessage = `以下必填欄位尚未填寫：${fieldNames}`;

        // 顯示詳細錯誤訊息
        console.warn('表單驗證失敗:', invalidRequiredFields);
        alert(errorMessage);

        // 滾動到第一個錯誤欄位
        this.scrollToFirstError();
      } else {
        console.warn('表單驗證失敗，請檢查必填欄位');
      }

      // 標記所有欄位為已觸摸，以顯示驗證錯誤
      this.formGroup.markAllAsTouched();
      return;
    }

    // 取得所有欄位的值
    const rawData = this.formGroup.value;

    // 根據 config 產生一個 mapping，每個欄位以 { type, value } 儲存
    const fieldsData: { [key: string]: { type: string; value: any } } = {};
    this.config.forEach(field => {
      fieldsData[field.name] = {
        type: field.type,
        value: rawData[field.name]
      };
    });

    // 轉換特定元件的 value 為 label
    this.transformValueToLabel(fieldsData);

    // 將 forminfo 與 fieldsData 合併，展開 fieldsData 為頂層屬性
    const dataToSave = {
      ...this.forminfo,
      formid: this.formid,
      parent_formid: this.parent_formid, // 新增：加入 parent_formid 參數
      ...fieldsData,
      // 新增：加入 formid 和 parent_formid 參數
    };

    console.log('送出資料：', dataToSave);

    // 如果有訂閱 formSubmitted 事件，則發射事件而不是直接儲存
    if (this.formSubmitted.observed) {
      console.log('發射 formSubmitted 事件');
      this.formSubmitted.emit(rawData);
      return;
    }

    // 呼叫 API 傳送資料到後端
    this.databaseService.sendDataToBackend(dataToSave, "form-container")
      .then(response => {
        console.log('Response from backend:', response);
        this.formfinished = true;
        // 發射記錄已新增事件，通知父組件更新列表
        this.recordAdded.emit(true);
      })
      .catch(error => {
        console.error('Error submitting data:', error);
      });
  }

  // 取得無效的必填欄位
  getInvalidRequiredFields(): FormFieldConfig[] {
    const invalidFields: FormFieldConfig[] = [];

    this.config.forEach(field => {
      if (field.required) {
        const control = this.formGroup.get(field.name);
        if (control && control.invalid) {
          invalidFields.push(field);
        }
      }
    });

    return invalidFields;
  }

  // 取得無效必填欄位的標籤文字，用於顯示錯誤訊息
  getInvalidRequiredFieldsText(): string {
    const invalidFields = this.getInvalidRequiredFields();
    return invalidFields.map(field => field.label).join('、');
  }

  // 滾動到第一個錯誤欄位
  scrollToFirstError(): void {
    const firstInvalidField = this.getInvalidRequiredFields()[0];
    if (firstInvalidField) {
      // 使用 setTimeout 確保 DOM 已更新
      setTimeout(() => {
        const element = document.querySelector(`[data-field-name="${firstInvalidField.name}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  // 檢查特定欄位是否無效（用於模板）
  isFieldInvalid(field: FormFieldConfig): boolean {
    if (!field.required || !this.formGroup) return false;

    const control = this.formGroup.get(field.name);
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitAttempted));
  }

  // 轉換特定元件的儲存值從 value 改為 label 或保持完整資料結構
  private transformValueToLabel(fieldsData: { [key: string]: { type: string; value: any } }): void {
    console.log('開始轉換表單值為標籤文字...');

    console.log('原始欄位資料:', fieldsData);

    this.config.forEach(field => {
      const fieldData = fieldsData[field.name];
      if (!fieldData) return;

      const originalValue = JSON.stringify(fieldData.value);

      switch (field.type) {
        case 'multi-checkbox':
          if (fieldData.value) {
            fieldData.value = this.transformMultiCheckboxValue(field, fieldData.value);
          }
          break;
        case 'radiobutton':
          if (fieldData.value) {
            fieldData.value = this.transformRadiobuttonValue(field, fieldData.value);
          }
          break;
        case 'department-selector':
          fieldData.value = this.transformDepartmentValue(field, fieldData.value);
          break;
        case 'select':
          if (fieldData.value) {
            fieldData.value = this.transformSelectValue(field, fieldData.value);
          }
          break;
        case 'personnel-selector':
          fieldData.value = this.transformPersonnelValue(field, fieldData.value);
          break;
        case 'dynamic-api-select':
          fieldData.value = this.transformDynamicApiSelectValue(field, fieldData.value);
          break;
        case 'approval-flow':
          // 關鍵修復：簽核流程資料需要保持完整結構
          fieldData.value = this.transformApprovalFlowValue(field, fieldData.value);
          break;
        // 其他元件類型保持原值
        default:
          break;
      }

      const newValue = JSON.stringify(fieldData.value);
      if (originalValue !== newValue) {
        console.log(`${field.type} 欄位 "${field.name}" 已轉換:`, {
          原值: originalValue,
          新值: newValue
        });
      }
    });

    console.log('表單值轉換完成');
  }

  // 轉換 multi-checkbox 的值
  private transformMultiCheckboxValue(field: FormFieldConfig, value: any): any {
    if (!Array.isArray(value) || !field.options) return value;

    // 處理 CheckboxValue[] 格式
    if (value.length > 0 && value[0].hasOwnProperty('option') && value[0].hasOwnProperty('selected')) {
      return value.map((item: any) => {
        // 在 field.options 中尋找對應的 label
        const option = field.options?.find((opt: any) => opt.value === item.option);
        return {
          ...item,
          option: option ? option.label : item.option
        };
      });
    }

    // 處理 Category[] 格式
    if (value.length > 0 && value[0].hasOwnProperty('name') && value[0].hasOwnProperty('selected')) {
      // 這種格式已經使用 name (label)，無需轉換
      return value;
    }

    return value;
  }

  // 轉換 radiobutton 的值
  private transformRadiobuttonValue(field: FormFieldConfig, value: any): any {
    if (!field.options) return value;

    // 處理簡單字串值
    if (typeof value === 'string') {
      const option = field.options?.find((opt: any) => opt.value === value);
      return option ? option.label : value;
    }

    // 處理 RadioButtonValue 格式
    if (value && typeof value === 'object' && value.hasOwnProperty('selectedValue')) {
      const option = field.options?.find((opt: any) => opt.value === value.selectedValue);
      return {
        ...value,
        selectedValue: option ? option.label : value.selectedValue
      };
    }

    return value;
  }

  // 轉換 department-selector 的值
  private transformDepartmentValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return {
        type: 'department-selector',
        value: '',
        id: ''
      };
    }

    // 如果已經是增強格式，直接返回
    if (typeof value === 'object' && value.hasOwnProperty('type') && value.hasOwnProperty('value')) {
      return value;
    }

    if (typeof value === 'string') {
      const department = this.departmentService.getDepartmentById(value);
      return {
        type: 'department-selector',
        value: department ? department.name : value,
        id: value
      };
    }

    return {
      type: 'department-selector',
      value: value,
      id: value
    };
  }

  // 轉換 select 的值
  private transformSelectValue(field: FormFieldConfig, value: any): any {
    if (!field.options) return value;

    const option = field.options?.find((opt: any) => opt.value === value);
    return option ? option.label : value;
  }

  // 轉換 personnel-selector 的值
  private transformPersonnelValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return {
        type: 'personnel-selector',
        value: '',
        id: ''
      };
    }

    // 如果已經是增強格式，直接返回
    if (typeof value === 'object' && value.hasOwnProperty('type') && value.hasOwnProperty('value')) {
      return value;
    }

    // 單選模式
    if (typeof value === 'string') {
      const userName = this.getPersonnelNameById(value);
      return {
        type: 'personnel-selector',
        value: userName,
        id: value
      };
    }

    // 多選模式
    if (Array.isArray(value)) {
      const userNames = value.map(id => this.getPersonnelNameById(id)).filter(name => name);
      return {
        type: 'personnel-selector',
        value: userNames,
        id: value // 保持原始 ID 陣列
      };
    }

    return {
      type: 'personnel-selector',
      value: value,
      id: value
    };
  }

  // 透過 ID 取得人員姓名
  private getPersonnelNameById(id: string): string {
    const user = this.departmentService.getUserById(id);
    return user ? user.name : id;
  }

  // 轉換 approval-flow 的值（關鍵修復）
  private transformApprovalFlowValue(field: FormFieldConfig, value: any): any {
    // 如果值為空，保持空值
    if (!value) {
      return value;
    }

    // 如果已經是完整的簽核流程資料結構，直接保持
    if (typeof value === 'object' && value.type === 'approval-flow' && value.value) {
      console.log('簽核流程資料已是完整格式，保持不變:', value);
      return value;
    }

    // 如果是簽核流程物件但沒有 type 包裝，進行包裝
    if (typeof value === 'object' && (value.flowId || value.steps)) {
      console.log('簽核流程資料需要包裝為標準格式:', value);
      return {
        type: 'approval-flow',
        value: value
      };
    }

    // 如果是字串 ID，嘗試從已載入的流程定義中獲取完整資料
    if (typeof value === 'string' && value) {
      console.log('簽核流程資料為字串 ID，嘗試轉換為完整格式:', value);
      // 這裡可以嘗試從快取或 API 獲取完整資料
      // 暫時保持原值，但加上 type 包裝
      return {
        type: 'approval-flow',
        value: value
      };
    }

    // 其他情況保持原值
    console.log('簽核流程資料保持原格式:', value);
    return value;
  }

  // 轉換 dynamic-api-select 的值
  private transformDynamicApiSelectValue(field: FormFieldConfig, value: any): any {
    if (!value) {
      return {
        type: 'dynamic-api-select',
        value: '',
        id: ''
      };
    }

    // 如果已經是增強格式，直接返回
    if (typeof value === 'object' && value.hasOwnProperty('type') && value.hasOwnProperty('value')) {
      return value;
    }

    if (typeof value === 'string') {
      // 嘗試從表單中的對應元件獲取已載入的選項資料
      const displayLabel = this.getDynamicApiSelectLabel(field, value);

      return {
        type: 'dynamic-api-select',
        value: displayLabel || value, // 如果找到標籤則使用，否則使用 ID
        id: value
      };
    }

    return {
      type: 'dynamic-api-select',
      value: value,
      id: value
    };
  }

  // 嘗試從動態API選擇器組件中獲取選項標籤
  private getDynamicApiSelectLabel(field: FormFieldConfig, selectedValue: string): string | null {
    try {
      // 嘗試從DOM中找到對應的select元素並獲取選中選項的文字
      const selectElement = document.querySelector(`select[name="${field.name}"]`) as HTMLSelectElement;
      if (selectElement) {
        const selectedOption = selectElement.querySelector(`option[value="${selectedValue}"]`) as HTMLOptionElement;
        if (selectedOption && selectedOption.textContent) {
          return selectedOption.textContent.trim();
        }
      }
    } catch (error) {
      console.warn('無法從DOM獲取動態API選擇器標籤:', error);
    }

    return null;
  }

  onViewAllData() {
    this.Showlist.emit(this.formName);
  }

  fillnewform(){
    this.fillform.emit(this.formName);
  }

  // 保存表單配置
  save(): void {
    // 檢查是否有表單欄位
    if (!this.config || this.config.length === 0) {
      console.warn('無法保存空表單，請先添加表單欄位');
      return;
    }

    // 捕獲包含檔案資料的配置
    const configWithFiles = this.config.map(field => {
      const configCopy = { ...field };

      // 如果是 file-display 或 file-upload-manager 類型，從表單控制項中獲取檔案資料
      if ((field.type === 'file-display' || field.type === 'file-upload-manager') && this.formGroup && this.formGroup.get(field.name)) {
        const fileData = this.formGroup.get(field.name)?.value;

        if (field.type === 'file-upload-manager' && fileData && fileData.files && Array.isArray(fileData.files) && fileData.files.length > 0) {
          // file-upload-manager 使用 FileUploadValue 格式
          configCopy.files = fileData.files;
          console.log('捕獲 file-upload-manager 檔案資料:', field.name, fileData.files);
        } else if (field.type === 'file-display' && fileData && Array.isArray(fileData) && fileData.length > 0) {
          // file-display 使用檔案陣列格式
          configCopy.files = fileData;
          console.log('捕獲 file-display 檔案資料:', field.name, fileData);
        }
      }

      return configCopy;
    });

    if (this.editMode && this.editFormId) {

      console.log("編輯模式");

      // 編輯模式：使用 save_form_config API
      const dataToSave = {
        companyid: this.companyid,
        id: this.editFormId,
        formName: this.formName,
        assistantid: this.assistantid,
        parent_formid: this.parent_formid, // 新增：加入 parent_formid 參數
        config: configWithFiles
      };

      const apiUrl = `${environment.apiUrl}/api/form-manager/save_form_config`;        this.http.post(apiUrl, dataToSave).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.formSaved.emit(true);
            alert(this.i18nService.translate('dynamic_form.config_update_success'));
          } else {
            console.error('API 回應失敗:', response);
            alert(this.i18nService.translate('dynamic_form.config_update_failed'));
          }
        },
        error: (error) => {
          console.error('編輯表單配置失敗:', error);

          let errorMessage = this.i18nService.translate('form.dynamic.update_failed');
          if (error.status === 400) {
            errorMessage = this.i18nService.translate('form.dynamic.request_error');
          } else if (error.status === 404) {
            errorMessage = this.i18nService.translate('form.dynamic.config_not_found');
          } else if (error.status === 409) {
            errorMessage = this.i18nService.translate('form.dynamic.data_conflict');
          } else if (error.status === 403) {
            errorMessage = this.i18nService.translate('form.dynamic.no_permission');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          alert(errorMessage);
        }
      });
    } else {

      console.log("新增模式");

      // 新增模式：使用原本的 API
      const dataToSave = {
        companyid: this.companyid,
        formName: this.formName,
        assistantid: this.assistantid,
        parent_formid: this.parent_formid, // 新增：加入 parent_formid 參數
        config: configWithFiles
      };

      this.databaseService.sendDataToBackend(dataToSave, "form-config")
        .then(response => {
          console.log('新增表單配置成功:', response);
          this.formSaved.emit(true);
          this.recordAdded.emit(true);
        })
        .catch(error => {
          console.error('新增表單配置失敗:', error);
        });
    }
  }

  // 新增方法：同步檔案資料到配置（預覽模式下使用）
  private syncFileDataToConfig(formValues: any): void {
    let configChanged = false;

    // 檢查每個 file-display 和 file-upload-manager 欄位
    this.config.forEach(field => {
      if ((field.type === 'file-display' || field.type === 'file-upload-manager') && formValues[field.name]) {
        const fileData = formValues[field.name];

        // 處理不同格式的檔案資料
        let processedFileData;
        if (field.type === 'file-upload-manager' && fileData.files) {
          // file-upload-manager 返回 FileUploadValue 格式
          processedFileData = fileData.files;
        } else if (Array.isArray(fileData)) {
          // file-display 返回檔案陣列
          processedFileData = fileData;
        } else {
          return; // 不是有效的檔案資料格式
        }

        // 檢查是否為有效的檔案資料陣列
        if (Array.isArray(processedFileData) && processedFileData.length > 0) {
          // 檢查檔案資料是否有變化
          const currentFiles = field.files || [];
          const hasChanges = JSON.stringify(currentFiles) !== JSON.stringify(processedFileData);

          if (hasChanges) {
            field.files = [...processedFileData];
            configChanged = true;
            console.log(`檔案資料已同步到配置 - 欄位: ${field.name}`, processedFileData);
          }
        }
      }
    });

    // 如果配置有變化，發送事件
    if (configChanged) {
      // 延遲發送事件，避免太頻繁的更新
      setTimeout(() => {
        this.configChanged.emit([...this.config]);
        console.log('發送配置變更事件（檔案資料同步）', this.config);
      }, 500);
    }
  }

  //有設定  this.getuseridlist = true 時，才需要解析流程欄位
  private async checkAndResolveApprovalFlow(): Promise<void> {
    if (!this.config) return;

    // 查找簽核流程欄位
    const approvalFlowField = this.config.find(field => field.type === 'approval-flow');
    if (!approvalFlowField) return;

    console.log('發現簽核流程欄位，開始自動解析:', approvalFlowField);

    var flowid = approvalFlowField.approvalFlowId;

    if (!flowid) {
      return;
    }

    // 準備填寫人資訊
    // 從 AuthService 取得使用者資訊
    const userInfo = this.authService.getUserInfo();

    // 取得使用者部門資訊
    let departmentId = '';
    let departmentName = '';

    try {
      const userDepartments = await this.getUserDepartmentFromAPI(userInfo.userid);
      if (userDepartments && userDepartments.length > 0) {
        // 取第一個部門作為主要部門
        //TODO：這邊要思考一下，是否要支援多部門
        departmentId = userDepartments[0].id;
        departmentName = userDepartments[0].name;
        console.log('成功取得使用者部門資訊:', userDepartments);
      } else {
        console.warn('未取得填表者的部門資訊');
        // alert(this.i18nService.translate('dynamic_form.department_info_failed'));
        return;
      }
    } catch (error) {
      console.warn('未取得填表者的部門資訊:', error);
      // alert(this.i18nService.translate('dynamic_form.department_info_failed'));
      return;
    }

    this.initiatorInfo = {
      userId: userInfo.userid,
      userName: userInfo.username,
      departmentId: departmentId,
      departmentName: departmentName
    };

    console.log('填寫人資訊:', this.initiatorInfo);

  }


  // 新增方法：透過 API 取得使用者部門資訊
  private async getUserDepartmentFromAPI(userid: string): Promise<{ id: string; name: string }[]> {
    const apiUrl = `${environment.apiUrl}/api/departments/user-departments`;

    const params = {
      companyid: environment.companyid,
      assistantNo: "ALL", //這邊使用整間公司的組織資訊
      userid: userid
    };

    console.log('呼叫使用者部門 API:', apiUrl, params);

    try {
      const response = await this.http.get<{
        success: boolean;
        data: { id: string; name: string }[];
        error?: string;
        message?: string;
      }>(apiUrl, { params }).toPromise();

      if (response && response.success && response.data) {
        return response.data;
      } else {
        console.warn('API 回應異常:', response);
        return [];
      }
    } catch (error) {
      console.warn('呼叫使用者部門 API 失敗:', error);
      throw error;
    }
  }

  // 處理多人簽名更新
  onSignaturesUpdated(fieldName: string, signatures: any[]): void {
    if (this.formGroup) {
      // 將簽名資料轉換為表單可用的格式
      const signatureData = {
        participants: signatures,
        signedCount: signatures.filter(s => s.signature !== null).length,
        totalCount: signatures.length,
        updatedAt: new Date().toISOString()
      };

      // 更新表單控制項的值
      const control = this.formGroup.get(fieldName);
      if (control) {
        control.setValue(JSON.stringify(signatureData));
        control.markAsTouched();
      }

      console.log(`多人簽名欄位 ${fieldName} 已更新:`, signatureData);
    }
    }
  // 轉換檔案資料格式的方法
  convertToFileItems(files: any[]): FileItem[] {
    return files.map(file => ({
      id: file.id,
      name: file.name || file.originalName,
      originalName: file.originalName || file.name,
      blobName: file.blobName, // 使用 name 作為 blobName
      size: file.size,
      type: file.type,
      uploadedAt: new Date(file.uploadedAt),
      uploading: file.uploading || false,
      uploadProgress: file.uploadProgress || 100,
      error: file.error
    }));
  }

  // ===== 階段化表單方法 =====

  loadStageConfiguration(): void {
    // 從表單配置載入階段資訊
    // 這裡先使用模擬的邏輯，實際應該從後端載入
    const savedStageConfig = localStorage.getItem(`stageConfig_${this.formName}`);
    if (savedStageConfig) {
      try {
        const stageData = JSON.parse(savedStageConfig);
        this.enableStageMode = stageData.enableStageMode || false;
        this.formStages = stageData.formStages || [];
        if (this.enableStageMode && this.formStages.length > 0) {
          this.initializeCurrentStage();
        }
      } catch (error) {
        console.error('載入階段配置失敗:', error);
      }
    }
  }

  initializeCurrentStage(): void {
    // 根據用戶權限和表單狀態決定當前階段
    this.currentStageIndex = this.determineUserStage();
    this.currentStage = this.formStages[this.currentStageIndex] || null;
  }

  determineUserStage(): number {
    // 簡化邏輯：總是從第一個階段開始
    // 實際實作應該根據用戶權限和表單狀態來決定
    return 0;
  }

  getFilteredSectionsByStage(): any[] {
    if (!this.enableStageMode || !this.currentStage) {
      return this.groupedSections;
    }

    // 過濾出當前階段和已完成階段的欄位
    return this.groupedSections.map(section => ({
      ...section,
      fields: section.fields.filter(field => this.isFieldVisibleInCurrentStage(field))
    })).filter(section => section.fields.length > 0);
  }

  isFieldVisibleInCurrentStage(field: FormFieldConfig): boolean {
    if (!this.enableStageMode) return true;

    // 沒有階段歸屬的欄位總是可見
    if (!field.stageId) return true;

    // 當前階段的欄位可編輯
    if (field.stageId === this.currentStage?.id) return true;

    // 已完成階段的欄位只讀可見
    const fieldStageIndex = this.formStages.findIndex(s => s.id === field.stageId);
    return fieldStageIndex < this.currentStageIndex;
  }

  completeCurrentStage(): void {
    if (!this.validateCurrentStage()) {
      return;
    }

    // 標記當前階段為完成
    if (this.currentStage) {
      this.currentStage.status = 'completed';
      this.currentStage.completedBy = this.Username;
      this.currentStage.completedAt = new Date();
    }

    // 進入下一階段
    if (this.currentStageIndex < this.formStages.length - 1) {
      this.currentStageIndex++;
      this.currentStage = this.formStages[this.currentStageIndex];
      this.currentStage.status = 'in_progress';
    }

    // 保存階段狀態
    this.saveStageProgress();
  }

  validateCurrentStage(): boolean {
    // 驗證當前階段的必填欄位
    const currentStageFields = this.getCurrentStageFields();
    return currentStageFields.every(field => this.isFieldValid(field));
  }

  getCurrentStageFields(): FormFieldConfig[] {
    if (!this.currentStage) return [];
    return this.config.filter(field => field.stageId === this.currentStage!.id);
  }

  isFieldValid(field: FormFieldConfig): boolean {
    const control = this.formGroup.get(field.name);
    return !field.required || (control && control.value && control.valid);
  }

  canCompleteCurrentStage(): boolean {
    return this.currentStage?.status === 'in_progress' &&
           this.hasPermissionForCurrentStage();
  }

  hasPermissionForCurrentStage(): boolean {
    // 簡化邏輯：總是返回 true
    // 實際實作應該檢查用戶是否有權限完成當前階段
    return true;
  }

  canGoToPreviousStage(): boolean {
    return this.currentStageIndex > 0 &&
           this.hasPermissionToModifyPreviousStage();
  }

  hasPermissionToModifyPreviousStage(): boolean {
    // 簡化邏輯：總是返回 false
    // 實際實作應該檢查用戶是否有權限修改前一階段
    return false;
  }

  goToPreviousStage(): void {
    if (this.canGoToPreviousStage()) {
      this.currentStageIndex--;
      this.currentStage = this.formStages[this.currentStageIndex];
    }
  }

  isLastStage(): boolean {
    return this.currentStageIndex === this.formStages.length - 1;
  }

  saveStageProgress(): void {
    // 保存階段進度到後端或本地儲存
    const stageData = {
      enableStageMode: this.enableStageMode,
      formStages: this.formStages,
      currentStageIndex: this.currentStageIndex
    };
    localStorage.setItem(`stageProgress_${this.formName}`, JSON.stringify(stageData));
  }

  // 取消表單
  cancel(): void {
    this.formCancelled.emit();
  }
}
